import { adoptedDestinationId, hasAnyOverride } from "../cableEditGeometry";
import type { AdoptedDevice } from "../cableEditGeometry";
import type { DescribeEditStep, EditStepSummary } from "./types";

interface KeyDelta {
  added: string[];
  removed: string[];
  changed: string[];
}

const NO_DELTA: KeyDelta = { added: [], removed: [], changed: [] };

const keyDelta = <T,>(
  from: Record<string, T> | undefined,
  to: Record<string, T> | undefined,
): KeyDelta => {
  // Every writer spreads the map it changes and leaves the rest alone, so one
  // reference check settles the fourteen fields a step did not touch.
  if (from === to) return NO_DELTA;
  const before = from ?? {};
  const after = to ?? {};
  const added: string[] = [];
  const changed: string[] = [];
  const removed: string[] = [];
  for (const key of Object.keys(after)) {
    if (!(key in before)) added.push(key);
    else if (!Object.is(before[key], after[key])) changed.push(key);
  }
  for (const key of Object.keys(before)) {
    if (!(key in after)) removed.push(key);
  }
  return { added, removed, changed };
};

const touched = (delta: KeyDelta): string[] => [...delta.added, ...delta.changed];

const isEmpty = (delta: KeyDelta): boolean =>
  !delta.added.length && !delta.removed.length && !delta.changed.length;

/** The one value they all share, or null when they disagree or there are none. */
const onlyValue = (values: (string | undefined)[]): string | null => {
  const distinct = new Set(values.filter((value): value is string => !!value));
  return distinct.size === 1 ? [...distinct][0] : null;
};

const summary = (
  kind: EditStepSummary["kind"],
  count: number,
  circuitId: string | null = null,
  destinationIds: string[] = [],
): EditStepSummary => ({ kind, count, circuitId, destinationIds });

/**
 * The step is the difference between two documents, so it cannot drift from
 * what actually happened — it IS what happened. Nothing has to be labelled at
 * write time, and a mutator added later is described without being told to.
 *
 * The order below is the order of what he would SAY he did, not the order of
 * the fields: `assignDeviceTo` restores a device and re-parents it in one
 * write, and that is a move, not a restore.
 */
export const describeEditStep: DescribeEditStep = ({ from, to }) => {
  // A BARE document, not merely an empty one. `resetAll` is the only writer
  // that produces `{}`; every other mutator spreads what was there, so putting
  // the last struck device back leaves `removedDevices: {}` behind and must not
  // read as wiping the morning.
  if (hasAnyOverride(from) && !Object.keys(to).length) {
    return summary("resetAll", 0);
  }

  // FIRST of the field checks, ahead of the device moves it carries: deleting a
  // run he created re-points every device on it in the SAME write, and what he
  // did was delete the run, not move eleven devices.
  const runs = keyDelta(from.addedCircuits, to.addedCircuits);
  const made = touched(runs);
  if (made.length) {
    return summary("addRun", made.length, made.length === 1 ? made[0] : null);
  }
  if (runs.removed.length) {
    return summary(
      "removeRun",
      runs.removed.length,
      runs.removed.length === 1 ? runs.removed[0] : null,
    );
  }

  const removals = keyDelta(from.removedDevices, to.removedDevices);
  if (removals.added.length) {
    return summary("removeDevices", removals.added.length, null, removals.added);
  }

  const groups = keyDelta(from.groupOverrides, to.groupOverrides);
  const moved = touched(groups);
  if (moved.length) {
    const onto = onlyValue(moved.map((id) => to.groupOverrides?.[id]));
    return summary("moveDevices", moved.length, onto, moved);
  }

  if (removals.removed.length) {
    return summary(
      "restoreDevices",
      removals.removed.length,
      null,
      removals.removed,
    );
  }

  const adopted = keyDelta<AdoptedDevice>(from.adoptedDevices, to.adoptedDevices);
  const takenOn = touched(adopted);
  if (takenOn.length) {
    const onto = onlyValue(
      takenOn.map((elementId) => to.adoptedDevices?.[elementId]?.circuitId),
    );
    return summary(
      "adoptDevices",
      takenOn.length,
      onto,
      takenOn.map(adoptedDestinationId),
    );
  }

  const routes = keyDelta(from.routeOverrides, to.routeOverrides);
  const stranded = keyDelta(from.strandedByCircuit, to.strandedByCircuit);
  if (!isEmpty(routes) || !isEmpty(stranded)) {
    const reshaped = [...touched(routes), ...routes.removed];
    const strandedOn = [...touched(stranded), ...stranded.removed];
    return summary(
      "reshape",
      reshaped.length,
      strandedOn.length === 1 ? strandedOn[0] : null,
      reshaped,
    );
  }

  // Before the feed point: striking a board re-points every circuit it fed in
  // the same write, and what he did was strike the board.
  const boards = [
    keyDelta(from.sourceOverrides, to.sourceOverrides),
    keyDelta(from.removedSources, to.removedSources),
    keyDelta(from.addedSources, to.addedSources),
    keyDelta(from.sourceNames, to.sourceNames),
  ];
  const boardLabels = new Set(
    boards.flatMap((delta) => [...touched(delta), ...delta.removed]),
  );
  if (boardLabels.size) return summary("board", boardLabels.size);

  // Before the feed point, and for the same reason boards come before it: an
  // area governs a region, so what he did was draw the area. Its own list, not
  // a keyed map — the order of areas is what settles two that overlap.
  if (from.areaFeeds !== to.areaFeeds) {
    const before = from.areaFeeds ?? [];
    const after = to.areaFeeds ?? [];
    if (before.length !== after.length) {
      return summary("feedArea", Math.abs(after.length - before.length));
    }
  }

  const feeds = keyDelta(from.feedPointByCircuit, to.feedPointByCircuit);
  const fed = [...touched(feeds), ...feeds.removed];
  if (fed.length) {
    return summary("feedPoint", fed.length, fed.length === 1 ? fed[0] : null);
  }

  const heights = [
    keyDelta(from.boardHeights, to.boardHeights),
    keyDelta(from.installationHeights, to.installationHeights),
    keyDelta(from.trayHeights, to.trayHeights),
  ];
  const heightsChanged = heights.reduce(
    (count, delta) => count + touched(delta).length + delta.removed.length,
    0,
  );
  if (heightsChanged) return summary("height", heightsChanged);

  if (from.cableTypes !== to.cableTypes) return summary("cableType", 1);
  const legs = keyDelta(from.cableTypeByLeg, to.cableTypeByLeg);
  const releged = [...touched(legs), ...legs.removed];
  if (releged.length) {
    return summary("cableType", releged.length, releged.length === 1 ? releged[0] : null);
  }

  const types = keyDelta(from.cableTypeByCircuit, to.cableTypeByCircuit);
  const retyped = [...touched(types), ...types.removed];
  if (retyped.length) {
    return summary(
      "cableType",
      retyped.length,
      retyped.length === 1 ? retyped[0] : null,
    );
  }

  return summary("edit", 0);
};
